/**
 * 検索（docs/01-requirements.md §5.7）。
 *
 * - 入力は 300ms の debounce。打鍵のたびに投げない。
 * - カテゴリ・対象日範囲と組み合わせて絞り込める。
 * - 結果には一致箇所を含む本文抜粋（サーバーが作る snippet）を出す。
 *   **snippet は innerHTML へ入れない。** プレーンテキストとして扱い、
 *   強調はテキストノードを分割して `<mark>` を組み立てる。
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@app/api/client';
import { AppHeader } from '@app/components/AppHeader';
import { ErrorNotice } from '@app/components/ErrorNotice';
import { subjectLabel } from '@app/components/subject-label';
import { useCategories } from '@app/components/useCategories';
import { displayTitle } from '@shared/markdown-input';
import type { SearchHit } from '@shared/types';

const DEBOUNCE_MS = 300;

/** 検索語（空白区切り）を snippet 内で強調する。HTML は組み立てない。 */
function highlight(text: string, query: string): ReactNode {
  const terms = query
    .split(/\s+/)
    .map((term) => term.trim())
    .filter((term) => term !== '')
    .sort((a, b) => b.length - a.length);
  if (terms.length === 0) return text;

  const lower = text.toLowerCase();
  const parts: ReactNode[] = [];
  let index = 0;
  let key = 0;

  while (index < text.length) {
    let hitAt = -1;
    let hitLength = 0;
    for (const term of terms) {
      const at = lower.indexOf(term.toLowerCase(), index);
      if (at >= 0 && (hitAt < 0 || at < hitAt)) {
        hitAt = at;
        hitLength = term.length;
      }
    }
    if (hitAt < 0) {
      parts.push(text.slice(index));
      break;
    }
    if (hitAt > index) parts.push(text.slice(index, hitAt));
    parts.push(<mark key={key++}>{text.slice(hitAt, hitAt + hitLength)}</mark>);
    index = hitAt + hitLength;
  }
  return parts;
}

export function SearchPage() {
  const categories = useCategories();
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [searched, setSearched] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = query.trim();

  useEffect(() => {
    if (trimmed === '') {
      setHits([]);
      setSearched(false);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      api
        .search(
          {
            q: trimmed,
            ...(categoryId != null ? { category_id: categoryId } : {}),
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
          },
          controller.signal,
        )
        .then((response) => {
          setHits(response.hits);
          setError(null);
          setSearched(true);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setError(err);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed, categoryId, dateFrom, dateTo]);

  const nameOf = categories.nameOf;
  const results = useMemo(
    () =>
      hits.map((hit) => ({
        hit,
        title: displayTitle(hit.note.title),
        categoryName: nameOf(hit.note.category_id),
      })),
    [hits, nameOf],
  );

  return (
    <div className="page">
      <AppHeader title="検索" backTo="/" />

      <div className="toolbar toolbar--column">
        <input
          ref={inputRef}
          type="search"
          className="al-input"
          value={query}
          placeholder="タイトル・本文を検索"
          aria-label="検索語"
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="toolbar">
          <label className="field field--inline">
            <span className="field__label">カテゴリ</span>
            <select
              className="al-select al-select--compact"
              value={categoryId ?? ''}
              onChange={(event) =>
                setCategoryId(event.target.value === '' ? null : Number(event.target.value))
              }
            >
              <option value="">すべて</option>
              {categories.categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field field--inline">
            <span className="field__label">対象日</span>
            <input
              className="al-input al-input--compact"
              type="date"
              value={dateFrom}
              aria-label="対象日の開始"
              onChange={(event) => setDateFrom(event.target.value)}
            />
            <span aria-hidden="true">〜</span>
            <input
              className="al-input al-input--compact"
              type="date"
              value={dateTo}
              aria-label="対象日の終了"
              onChange={(event) => setDateTo(event.target.value)}
            />
          </label>
        </div>
      </div>

      <main className="page__body">
        {error != null && <ErrorNotice error={error} />}
        {loading && <p className="muted centered-note">検索中…</p>}
        {!loading && trimmed === '' && (
          <p className="muted centered-note">検索語を入力してください。</p>
        )}
        {!loading && searched && hits.length === 0 && error == null && (
          <p className="muted centered-note">一致するノートはありませんでした。</p>
        )}

        <ul className="search-results">
          {results.map(({ hit, title, categoryName }) => (
            <li key={hit.note.id} className="search-result">
              <Link to={`/notes/${hit.note.id}`} className="search-result__link">
                <div className="note-card__meta">
                  <span className="note-card__date">
                    {subjectLabel(hit.note.subject_date, hit.note.subject_date_end)}
                  </span>
                  {categoryName && (
                    <span className="al-chip al-chip--lilac mm-category-badge">{categoryName}</span>
                  )}
                </div>
                <div className="search-result__title">{title}</div>
                <p className="search-result__snippet">{highlight(hit.snippet, trimmed)}</p>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
